'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'

import type { BlogPostMeta } from '@/lib/blog/schema'

import { MoodPostCard } from './mood-post-card'

/** Posts revealed per scroll batch — divisible by 3 and 4 so rows stay full. */
const BATCH_SIZE = 12

/**
 * The mood archive's post listing — a responsive card grid (1/2/3/4 columns by
 * viewport) that reveals {@link BATCH_SIZE}-post batches as the reader scrolls
 * All posts are static build data already in the page
 * payload, so revealing is pure client-side slicing — an IntersectionObserver
 * sentinel below the grid grows the visible count; no fetching, no spinner. The
 * first batch is part of the server HTML; a visually-hidden live region
 * announces progress for assistive tech.
 */
export function MoodPostGrid({ posts }: { posts: BlogPostMeta[] }) {
    const t = useTranslations('blogHome.wanderByMood')
    const [visibleCount, setVisibleCount] = useState(Math.min(BATCH_SIZE, posts.length))
    const sentinelRef = useRef<HTMLDivElement | null>(null)
    const allVisible = visibleCount >= posts.length

    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel) {
            return // every post already visible — nothing to observe
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setVisibleCount((count) => Math.min(count + BATCH_SIZE, posts.length))
                }
            },
            // Pre-trigger just before the reader reaches the bottom, so the next
            // batch is in place by the time they get there (the sentinel is 1px —
            // with no margin it only fires at the exact page bottom).
            { rootMargin: '200px 0px' },
        )
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [allVisible, posts.length])

    return (
        <div>
            <p aria-live="polite" className="sr-only">
                {t('showingCount', { visible: visibleCount, total: posts.length })}
            </p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {posts.slice(0, visibleCount).map((post) => (
                    <MoodPostCard key={post.slug} post={post} />
                ))}
            </div>
            {allVisible ? null : <div ref={sentinelRef} aria-hidden="true" className="h-px" />}
        </div>
    )
}
